import { get } from 'lodash';
import { getSsoAuthorizationUrl, getSsoChangeCredentialsUrl } from 'apps/sso/actions/sso';
import { randomString } from 'utils/randomString';
import { addQueryParams } from 'utils/url';
import { wa } from 'apps/analytics';
import { BetaFeatures } from 'apps/login/consts';
import { BetaFeatures as SettingsBetaFeatures } from 'apps/settings/consts';
import { setNoRouteAccess } from 'apps/user/actions/user';
import { clientCookies } from 'utils/cookies';
import config from 'config';
import errorHandler from 'utils/errorHandler';

export const redirectToSSO = (entryPoint) =>
  (nextState, replace, path, extras) => {
    const { store, network, history } = extras;
    const { dispatch } = store;
    const state = store.getState();
    const { http: { origin } } = state;
    const userBetaFeatures = get(state, 'user.betaFeatures', []);

    if (!userBetaFeatures.includes('SSO_INTEGRATION') || !userBetaFeatures.includes('SSO_FULL_REDIRECT')) {
      return Promise.resolve();
    }

    const { directPostcode, proceed } = nextState.location.query;
    if (directPostcode && !proceed) {
      // Display a page with Scribe documents before registering
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const nonce = randomString(16);
      const ssoState = randomString(16);
      const redirectUri = `${origin}/webshop/sso/spa-post-login.go`;
      const postLoginUri = getCurrentUri(history, network);
      const vipCookieValue = clientCookies.get('VR');

      dispatch(getSsoAuthorizationUrl(nonce, ssoState, redirectUri, postLoginUri)).then((authorizeUrl) => {
        const url = addQueryParams(authorizeUrl, {
          client_id: 'webshop-ocean',
          redirect_uri: redirectUri,
          response_type: 'code',
          scope: 'openid',
          entry_point: entryPoint,
          tid: wa.getPageViewId(),
          nonce,
          state: ssoState,
          ...(vipCookieValue && { vreg: vipCookieValue }),
          ...(directPostcode && { direct: true, postcode: directPostcode }),
        });

        network.redirectTo({ to: url, reload: true });
        dispatch(setNoRouteAccess('DO_NOT_REDIRECT'));
        reject();
      });
    });
  };

export const redirectToSSOChangeCredentials = () =>
  (nextState, replace, path, extras) => {
    const { store, network } = extras;
    const { dispatch } = store;
    const state = store.getState();
    const { http: { origin } } = state;

    return new Promise((resolve, reject) => {
      const redirectUri = `${origin}/settings`;

      dispatch(getSsoChangeCredentialsUrl()).then((changeCredentialsUrl) => {
        const url = addQueryParams(changeCredentialsUrl, {
          client_id: 'webshop-ocean',
          redirect_uri: redirectUri,
        });
        network.redirectTo({ to: url, reload: true });
        dispatch(setNoRouteAccess('DO_NOT_REDIRECT'));
        reject();
      });
    });
  };

export const redirectToSSOLoginElevated = () =>
  (nextState, replace, path, extras) => {
    const { store } = extras;
    const state = store.getState();
    const userBetaFeatures = get(state, 'user.betaFeatures', []);

    if (!userBetaFeatures.includes(BetaFeatures.SSO_ELEVATED_LOGIN)) {
      return true;
    }

    const redirectUrl = get(nextState, 'location.query.redirect', '/');
    replace(redirectUrl);
    return false;
  };

export const redirectToChangeCredentials = () =>
  (nextState, replace, path, extras) => {
    const { store } = extras;
    const state = store.getState();
    const userBetaFeatures = get(state, 'user.betaFeatures', []);

    if (!userBetaFeatures.includes(SettingsBetaFeatures.SSO_CHANGE_CREDENTIALS)) {
      return true;
    }

    replace('/change-credentials');
    return false;
  };

export function getCurrentUri(history, network) {
  const fallbackUrl = '/';

  try {
    const currentLocation = history.getCurrentLocation();
    const currentLocationSearch = currentLocation.search;
    const redirectLinkFromRequestQuery = get(network, 'request.query.redirect');
    const redirectLinkFromUrlSearchParams = getUrlSearchParam(currentLocationSearch, 'redirect');

    if (redirectLinkFromRequestQuery) {
      return redirectLinkFromRequestQuery;
    }

    if (redirectLinkFromUrlSearchParams) {
      return redirectLinkFromUrlSearchParams;
    }

    if (currentLocation) {
      return `${currentLocation.pathname}${currentLocation.search}`;
    }

    return fallbackUrl;
  } catch (err) {
    errorHandler(err);
    return fallbackUrl;
  }
}

function getUrlSearchParam(locationSearch, paramName) {
  if (config.isServer && !URLSearchParams) {
    return null;
  }

  const urlParams = new URLSearchParams(locationSearch);
  return urlParams.get(paramName);
}
